import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { createTestToneOutput, type TestToneOutput } from 'utils/audio/playTestTone';
import { trackError } from 'utils/trackError';

export type TestToneSource = 'card' | 'settings';

interface TestToneContextValue {
  playingSource: TestToneSource | null;
  /** A call while another tone plays is ignored. */
  play: (sinkId: string, source: TestToneSource) => Promise<void>;
}

const TestToneContext = createContext<TestToneContextValue | undefined>(undefined);

/** Shares one audio output between the test tone buttons of a screen. */
export const TestToneProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const outputRef = useRef<TestToneOutput | null>(null);
  const playingRef = useRef(false);
  const [playingSource, setPlayingSource] = useState<TestToneSource | null>(null);

  useEffect(() => {
    const output = createTestToneOutput();
    outputRef.current = output;
    return () => {
      outputRef.current = null;
      void output.close();
    };
  }, []);

  const play = useCallback(
    async (sinkId: string, source: TestToneSource) => {
      const output = outputRef.current;
      if (!output || playingRef.current) return;
      playingRef.current = true;
      setPlayingSource(source);
      try {
        await output.play(sinkId);
      } catch (error) {
        trackError(error, { context: 'audioDevice', action: 'playTestTone' });
        toast.error(t('audio.settings.testToneFailed'));
      } finally {
        playingRef.current = false;
        if (outputRef.current) setPlayingSource(null);
      }
    },
    [t]
  );

  const value = useMemo<TestToneContextValue>(
    () => ({ playingSource, play }),
    [playingSource, play]
  );

  return <TestToneContext.Provider value={value}>{children}</TestToneContext.Provider>;
};

export function useTestTone(): TestToneContextValue {
  const context = useContext(TestToneContext);
  if (!context) {
    throw new Error('useTestTone must be used within a TestToneProvider');
  }
  return context;
}
